import React, { useEffect, useId, useRef, useState } from 'react';
import { call, errText, recaptchaToken } from './firebase';

// Proving the phone by a texted code (functions/src/webIdentity.ts). The vendor
// portal's copy (sudsvendor/src/verify.tsx) also has the email-code screen; the jobs
// site signs in by emailed code in App.tsx instead.

const RESEND_SEC = 30;

/** "+15165550123" → "(516) 555-0123". */
export function formatPhone(e164: string | null | undefined): string {
  const d = String(e164 ?? '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(e164 ?? '');
}

function useCooldown(): [number, () => void] {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!left) return;
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return [left, () => setLeft(RESEND_SEC)];
}

const digits6 = (v: string) => v.replace(/\D/g, '').slice(0, 6);

/**
 * The signed-in account proves a US phone: the server texts a code (webSendPhoneCode)
 * and checks it (webConfirmPhoneCode). `onVerified` gets the proved E.164 number.
 */
export function VerifyPhone({ current, title, intro, onVerified, onCancel }: {
  current?: string | null;
  title: string;
  intro: string;
  onVerified: (phone: string) => void;
  onCancel?: () => void;
}) {
  const [phone, setPhone] = useState(current ? formatPhone(current) : '');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'send' | 'verify' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cooldown, startCooldown] = useCooldown();
  const uid = useId();
  const captchaId = useRef(`captcha-${Math.random().toString(36).slice(2)}`).current;
  const typedDigits = phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');

  const send = async () => {
    setBusy('send'); setErr(null);
    try {
      const token = await recaptchaToken(captchaId);
      const r: any = await call('webSendPhoneCode', { phone: typedDigits, recaptchaToken: token });
      setSentTo(r.phone); setCode(''); startCooldown();
    } catch (e) { setErr(errText(e, "We couldn't send a text just now. Please try again.")); } finally { setBusy(null); }
  };
  const verify = async () => {
    setBusy('verify'); setErr(null);
    try { const r: any = await call('webConfirmPhoneCode', { code }); onVerified(r.phone); } catch (e) { setErr(errText(e)); } finally { setBusy(null); }
  };

  return (
    <div className="card" style={{ maxWidth: 460, margin: '24px auto' }}>
      <h2>{title}</h2>
      <p className="muted">{intro}</p>
      {!sentTo ? (
        <>
          <label htmlFor={`${uid}phone`}>Mobile phone (US)</label>
          <input id={`${uid}phone`} type="tel" autoComplete="tel-national" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(516) 555-0123" />
          {err ? <div className="err">{err}</div> : null}
          <div className="row" style={{ marginTop: 14 }}>
            <button onClick={send} disabled={busy !== null || typedDigits.length !== 10}>{busy === 'send' ? 'Sending…' : 'Text me a code'}</button>
            {onCancel ? <button className="secondary" onClick={onCancel}>Cancel</button> : null}
          </div>
        </>
      ) : (
        <>
          <p className="muted">We texted a 6-digit code to <b>{formatPhone(sentTo)}</b>.</p>
          <label htmlFor={`${uid}code`}>Code</label>
          <input id={`${uid}code`} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(digits6(e.target.value))} placeholder="123456" />
          {err ? <div className="err">{err}</div> : null}
          <div className="row" style={{ marginTop: 14 }}>
            <button onClick={verify} disabled={busy !== null || code.length !== 6}>{busy === 'verify' ? 'Checking…' : 'Verify'}</button>
            <button className="secondary" onClick={send} disabled={busy !== null || cooldown > 0}>{cooldown > 0 ? `Send again in ${cooldown}s` : 'Send a new code'}</button>
          </div>
          <p className="muted" style={{ marginTop: 12 }}>
            <a href="#change" onClick={(e) => { e.preventDefault(); setSentTo(null); setErr(null); }}>Use a different number</a>
          </p>
        </>
      )}
      <div id={captchaId} />
    </div>
  );
}
